import { ArticlePage, Link } from '@graphql/index';
import {
  ArticleCardSize,
  ArticleCardVariant,
  ArticleType,
  Link as ButtonLink,
  RelatedContentProps,
} from '@stadionhq-v3/base-ui';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import { Locale } from '@core/constants/locales.constants';
import { Translations } from '@lib/dictionary-keys/__generated__';
import templateConfig from '@templateConfig';
import { ARTICLE_FORMATS } from '@core/types/pages/articlePage.types';
import { ArrowRight } from '@components/icons';
import { generateCardIcon } from '@utils/icons';
import ArticleCard from '@components/v3/molecules/article-card';
import { linkTransform } from '../atoms';
import { assetTransform } from '../atoms/asset.transform';

type RelatedContentTransformSettings = {
  locale: Locale;
  title: Translations['article']['label']['relatedContent'];
  cardVariant: ArticleCardVariant;
  cardSize: ArticleCardSize;
  subTitle?: string;
  cardCountType?: 'decimal' | 'decimal-leading-zero';
  latestNewsLabel: Translations['news']['label']['latestNews'];
  sponsoredBy: Translations['common']['label']['sponsoredBy'];
  publishDateFormat: string;
  onDark?: boolean;
};

const relatedContentTransform = (
  relatedContentData: CleanContentful<ArticlePage>[],
  settings: RelatedContentTransformSettings,
  overrideForRelatedContentCta?: CleanContentful<Link>
): RelatedContentProps => {
  const {
    cardCountType,
    cardSize,
    cardVariant,
    latestNewsLabel,
    locale,
    title,
    subTitle,
    sponsoredBy,
    publishDateFormat,
    onDark,
  } = settings;

  return {
    title,
    onDark,
    subTitle,
    cardCountType,
    altRelatedArticles: relatedContentData?.slice(0, 3).map((article) => (
      <ArticleCard
        key={article.id}
        {...{
          ...article,
          summary: undefined, // hide the summary text
          cardIcon: generateCardIcon(article.format as ArticleType),
          variant: cardVariant,
          size: cardSize,
          type: article.format as ArticleType,
          isFeatured: article?.isFeatured || false,
          slug: generateFullPathname({
            locale,
            route: templateConfig.navigation.routes.ARTICLE,
            params: { article: article.slug },
          }),
          id: article?.id,
          title: article?.articleTitle || '',
          image: article.image ? (
            <ContentfulImage {...article.image} />
          ) : (
            <ContentfulImage
              alt="Placeholder Image"
              width={420}
              height={360}
              url="https://images.ctfassets.net/qpz2vl40uleh/5t4ugdlfydzHls9NEfUAn5/80283eedacf3c4a8ef85a44a5209d2b6/y9DpT.jpeg"
            />
          ),
          author: article?.author && {
            firstName: article?.author?.firstName || '',
            secondName: article?.author?.secondName,
          },
          sponsor: article.sponsor
            ? {
                title: sponsoredBy,
                image: <ContentfulImage {...assetTransform(article.sponsor)} />,
              }
            : undefined,
          // teamIndicator: {},
          onDark:
            article.format === ARTICLE_FORMATS.VIDEO ||
            article.format === ARTICLE_FORMATS.GALLERY,
          category: {
            category: article?.category?.category || '',
            slug: generateFullPathname({
              locale,
              route: templateConfig.navigation.routes.NEWS_CATEGORY,
              params: { category: article.category?.slug },
            }),
          },
          publishDate: {
            date: article?.publishDate,
            format: publishDateFormat,
            locale,
          },
          duration: article?.featuredVideo?.video?.duration,
          imageCount: article?.featuredGallery?.images?.length,
        }}
      />
    )),
    relatedArticles: relatedContentData?.slice(0, 3).map((article) => ({
      ...article,
      summary: undefined, // hide the summary text
      cardIcon: generateCardIcon(article.format as ArticleType),
      variant: cardVariant,
      size: cardSize,
      type: article.format as ArticleType,
      isFeatured: article?.isFeatured as boolean,
      slug: generateFullPathname({
        locale,
        route: templateConfig.navigation.routes.ARTICLE,
        params: { article: article.slug },
      }),
      id: article?.id,
      title: article?.articleTitle || '',
      image: article.image ? (
        <ContentfulImage {...article.image} />
      ) : (
        <ContentfulImage
          alt="Placeholder Image"
          width={420}
          height={360}
          url="https://images.ctfassets.net/qpz2vl40uleh/5t4ugdlfydzHls9NEfUAn5/80283eedacf3c4a8ef85a44a5209d2b6/y9DpT.jpeg"
        />
      ),
      author: article?.author && {
        firstName: article?.author?.firstName || '',
        secondName: article?.author?.secondName || '',
      },
      sponsor: article.sponsor
        ? {
            title: sponsoredBy,
            image: <ContentfulImage {...assetTransform(article.sponsor)} />,
          }
        : undefined,
      // teamIndicator: {},
      onDark:
        article.format === ARTICLE_FORMATS.VIDEO ||
        article.format === ARTICLE_FORMATS.GALLERY,
      category: {
        // title: article?.category?.category,
        category: article?.category?.category || '',
        slug: generateFullPathname({
          locale,
          route: templateConfig.navigation.routes.NEWS_CATEGORY,
          params: { category: article.category?.slug },
        }),
      },
      publishDate: {
        date: article?.publishDate,
        format: publishDateFormat,
        locale,
      },
      duration: article?.featuredVideo?.video?.duration,
      imageCount: article?.featuredGallery?.images?.length,
    })),
    ctaButton: overrideForRelatedContentCta?.url ? (
      <ButtonLink
        {...linkTransform(overrideForRelatedContentCta, locale, <ArrowRight />)}
        title={overrideForRelatedContentCta.title || latestNewsLabel}
        size="large"
        onDark={onDark}
      />
    ) : (
      <></>
    ),
  };
};

export default relatedContentTransform;
